import { useCallback } from 'react';
import { useConfirm } from '@shared/components/Modal';
import { View } from '@shared/components/View';
import { MBMessageReactionButton } from '@shared/scenes/chat/thread/containers/messageReactionButton';
import { MBChannelTypeUtil } from '@shared/scenes/chat/thread/util/channelType';
import { fadeIn, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { MessageActionButton } from './MessageActionButton';
import classNames from './actionButtons.module.css';

export interface Props {
  message: API.ChatMessageFragment;
  channelSlug: string;
  isHovering: boolean;
  isMyMessage: boolean;
  onDelete(): void;
  onEdit(): void;
  includeEdit?: boolean;
  isSent?: boolean;
  reactionPickerAnchorName: string;
}

export function MBActionButtonsComponent({
  message,
  isHovering,
  isMyMessage,
  channelSlug,
  onEdit,
  onDelete,
  includeEdit = true,
  isSent = true,
  reactionPickerAnchorName,
}: Props) {
  const confirm = useConfirm();
  const isThread = MBChannelTypeUtil.isThread(channelSlug);
  // TODO(nf): reactions won't work in threads https://app.shortcut.com/coast/story/30746/threads-fix-reaction-mutation-in-chat-threads
  const showReact =
    isSent && !isThread && (isHovering || (!message.meta.reactions.length && !isMyMessage));
  const showEdit = isSent && isHovering && isMyMessage && !!message.content.length && includeEdit;
  const showDelete = isHovering && isMyMessage;
  const hasAttachments = message.meta.attachments.length > 0;
  const deleteMessageConfirmation = hasAttachments
    ? i18n('chat.delete.confirmationMessageWithAttachments')
    : i18n('chat.delete.confirmation');

  const confirmDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: i18n('chat.delete.title'),
      message: deleteMessageConfirmation,
      confirmLabel: i18n('chat.delete.confirmButton'),
      danger: true,
    });
    if (confirmed) {
      onDelete();
    }
  }, [confirm, onDelete, deleteMessageConfirmation]);

  if (!showReact && !showEdit && !showDelete) {
    return <View paddingHorizontal={1} />;
  }

  return (
    <View row shrinkWrap centerVertically className={[classNames.messageActionButtons, fadeIn]}>
      {showReact && (
        <MBMessageReactionButton
          message={message}
          channelSlug={channelSlug}
          anchorName={reactionPickerAnchorName}
        />
      )}
      {showEdit && <MessageActionButton onPress={onEdit} icon="pencil" iconSize={grid(3.5)} />}
      {showDelete && (
        <MessageActionButton onPress={confirmDelete} icon="trash" iconSize={grid(3.5)} />
      )}
    </View>
  );
}
